import { Policy } from "@design-controls/types";
import { useCurrentUser, useToastProvider } from "@qualio/ui-components";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { designControlsApi } from "../../api/designControls";
import { useCurrentProduct } from "../useCurrentProduct";
import { useCurrentConfigs } from "../useCurrentConfigs";
import { useTranslation } from "react-i18next";

const sortedArray = [
  "req1",
  "req2",
  "req3",
  "req4",
  "testCase1",
  "testCase2",
  "testCase3",
  "risk",
  "testCase",
  "testLog",
] as const;

export const useUpdatePolicy = () => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { id: productId } = useCurrentProduct();
  const navigate = useNavigate();
  const { showToast } = useToastProvider();
  const client = useQueryClient();
  const policyTypes = useCurrentConfigs().map((item) => item.type);

  return useMutation({
    mutationFn: (policies: Policy[]) => {
      const sortedPolicies = policies.sort(
        (a, b) => sortedArray.indexOf(a.type) - sortedArray.indexOf(b.type),
      );

      return Promise.all(
        sortedPolicies.map((policy) =>
          policyTypes.includes(policy.type)
            ? designControlsApi.updatePolicy(companyId, productId, policy)
            : designControlsApi.createPolicy(companyId, productId, policy),
        ),
      );
    },
    onSuccess: async () => {
      showToast({
        title: t("policies.notifications.updateSuccess.title"),
        description: t("policies.notifications.updateSuccess.description"),
        status: "success",
        "data-cy": "toast-policy-update-success",
      });
      await client.invalidateQueries(["PRODUCT_CONFIGS", companyId, productId]);
      navigate("..");
    },
    onError: () => {
      showToast({
        title: t("policies.notifications.updateError.title"),
        description: t("policies.notifications.updateError.description"),
        status: "error",
        "data-cy": "toast-policy-update-error",
      });
    },
  });
};
